import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table/table.types'
import { EMPTY_VALUE } from '@/constants'
import { Checklist, ChecklistTypeEnum } from '@/models/features'
import { ChecklistStatusExtendedEnum } from '@/pages/checklists/checklists-dashboard/checklists-dashboard.page.types'
import {
  getChecklistStatusColor,
  getChecklistStatusLabel,
  getChecklistTypeColor,
  getChecklistTypeLabel,
} from '@/pages/checklists/checklists-dashboard/checklists-dashboard.page.utils'
import { formatDateLong } from '@/utils/date'

export const UNIT_CHECKLISTS_TABLE_ID = 'unit-checklists'

export const unitChecklistsFiltersSchema = z.object({
  clientId: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  parentChecklistId: z.array(z.coerce.number()).optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: ChecklistStatusExtendedEnum.optional(),
  toDate: z.coerce.number().optional(),
  type: ChecklistTypeEnum.optional(),
})

export type UnitChecklistsFilters = z.infer<typeof unitChecklistsFiltersSchema>

export const unitChecklistsInitialFilters: UnitChecklistsFilters = {
  clientId: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  parentChecklistId: [],
  search: '',
  skip: 0,
  sortBy: 'dueDate',
  sortOrder: 'desc',
  status: ChecklistStatusExtendedEnum.enum.all,
  toDate: undefined,
  type: undefined,
}

interface GetUnitChecklistsColumnsProps {
  t: TFunction<['checklists']>
  timezone: string
}

export const getUnitChecklistsColumns = ({
  t,
  timezone,
}: GetUnitChecklistsColumnsProps): TableColumn<Checklist>[] => [
  {
    id: 'name',
    render: (checklist) => checklist.name,
    sortKey: 'name',
    title: t('checklists:dashboard.columns.name'),
    width: '28rem',
  },
  {
    id: 'status',
    render: (checklist) => (
      <Badge color={getChecklistStatusColor(checklist.status)} variant="light" size="small">
        {getChecklistStatusLabel(checklist.status, t)}
      </Badge>
    ),
    title: t('checklists:dashboard.columns.status'),
  },
  {
    id: 'dueDate',
    render: (checklist) =>
      checklist.dueDate ? formatDateLong(toZonedTime(checklist.dueDate, timezone)) : EMPTY_VALUE,
    sortKey: 'dueDate',
    title: t('checklists:dashboard.columns.dueDate'),
  },
  {
    id: 'type',
    render: (checklist) => (
      <Badge color={getChecklistTypeColor(checklist.type)} variant="light" size="small">
        {getChecklistTypeLabel(checklist.type, t)}
      </Badge>
    ),
    title: t('checklists:dashboard.columns.type'),
  },
  {
    id: 'resident',
    render: (checklist) => checklist.clientName,
    sortKey: 'clientName',
    title: t('checklists:dashboard.columns.resident'),
    width: '28rem',
  },
  {
    id: 'unitNumber',
    render: (checklist) => checklist.unit,
    title: t('checklists:dashboard.columns.unitNumber'),
  },
  {
    id: 'moveIn',
    render: (checklist) =>
      checklist.moveInDate
        ? formatDateLong(toZonedTime(checklist.moveInDate, timezone))
        : EMPTY_VALUE,
    sortKey: 'moveInDate',
    title: t('checklists:dashboard.columns.moveIn'),
  },
  {
    id: 'moveOut',
    render: (checklist) =>
      checklist.moveOutDate
        ? formatDateLong(toZonedTime(checklist.moveOutDate, timezone))
        : EMPTY_VALUE,
    title: t('checklists:dashboard.columns.moveOut'),
  },
]
